import type {
  RevenueProps,
  UserTraits,
} from '@src/Tracking/catalog';
import type {
  TrackingAdapter,
  TrackingEventName,
  TrackingPayload,
} from '@src/Providers/Tracking/TrackingContext';
import {getVendorConsent} from '@src/Providers/DidomiInitProvider/isTrackingEnabled';
import {runWhenBrazeReady} from '@src/Providers/BrazeProvider/brazeQueue';

/**
 * Braze web SDK adapter. The SDK is loaded asynchronously by BrazeProvider
 * (see useBraze.ts) — we dynamic-import the exports module to share that
 * single SDK instance and avoid bundle bloat.
 *
 * Every SDK call goes through runWhenBrazeReady(): the Braze SDK silently
 * discards anything invoked before initialize() completes, so calls are held
 * and replayed once it does. See brazeQueue.ts.
 */

const safeBraze = async () => {
  if (typeof window === 'undefined') return null;
  try {
    return await import('@src/Providers/BrazeProvider/braze-exports');
  } catch {
    return null;
  }
};

export const createBrazeAdapter = (): TrackingAdapter => ({
  name: 'braze',
  isEnabled: () => getVendorConsent().braze,

  track: async (event: TrackingEventName, props?: TrackingPayload) => {
    if (typeof event !== 'string') return;
    const braze = await safeBraze();
    if (!braze) return;
    try {
      // Held until initialize() has completed. The Braze SDK does NOT queue —
      // it discards anything logged pre-init and returns false (see
      // brazeQueue.ts for the SDK source). Without this, real users' events
      // were lost during the dynamic-import window with no error surfaced.
      runWhenBrazeReady(() => braze.logCustomEvent(event, props));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[braze] track failed', {event, err});
    }
  },

  identify: async (userId: string, traits?: UserTraits) => {
    const braze = await safeBraze();
    if (!braze) return;
    try {
      runWhenBrazeReady(() => {
        braze.changeUser(userId);
        const user = braze.getUser?.();
        if (!user || !traits) return;

        if (traits.email) user.setEmail(traits.email);
        if (traits.first_name) user.setFirstName(traits.first_name);
        if (traits.last_name) user.setLastName(traits.last_name);
        if (traits.country) user.setCountry(traits.country);
        if (traits.language) user.setLanguage(traits.language);
        Object.entries(traits).forEach(([k, v]) => {
          if (
            v === undefined ||
            v === null ||
            ['email', 'first_name', 'last_name', 'country', 'language'].includes(
              k,
            )
          ) {
            return;
          }
          try {
            user.setCustomUserAttribute(k, v as never);
          } catch {
            /* swallow per-attribute errors */
          }
        });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[braze] identify failed', err);
    }
  },

  trackRevenue: async (rev: RevenueProps) => {
    const braze = await safeBraze();
    if (!braze) return;
    try {
      // logPurchase now comes from the shared exports module rather than a
      // second import('@braze/web-sdk') — same instance, one source.
      runWhenBrazeReady(() =>
        braze.logPurchase(
          rev.product_id,
          rev.price,
          rev.currency ?? 'USD',
          rev.quantity ?? 1,
        ),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[braze] purchase failed', err);
    }
  },

  flush: async () => {
    const braze = await safeBraze();
    try {
      // Not queued: a flush is only meaningful once the SDK is up, and
      // buffered calls are flushed by markBrazeReady() anyway.
      braze?.requestImmediateDataFlush?.();
    } catch {
      /* swallow */
    }
  },
});
